export interface Beat {
  id: string;
  name: string;
  bpm: number;
  musical_key: string;
  genres: string[];
  duration_seconds: number | null;
  price_points: number;
  price_cents: number;
  status: string;
  favorite_count: number;
  listed_at: string | null;
  is_new: boolean;
  seller_name: string | null;
  preview_url: string | null;
  is_favorited: boolean;
}

export interface BeatFile {
  kind: 'master_wav' | 'stem' | 'preview_clip' | 'mp3' | 'stems_zip';
  label: string | null;
  bytes: number | null;
}

export interface BeatDetail extends Beat {
  files: BeatFile[];
}

export interface CatalogStats {
  live_now: number;
  new_this_week: number;
  points_balance: number;
  approx_beats_affordable: number;
  standard_price_points: number;
  standard_price_cents: number;
}

export interface LicenseVersion {
  id: string;
  kind: 'buyer' | 'seller';
  version: number;
  body_md: string;
  published_at: string;
}

export interface CheckoutResult {
  orderId: string;
  status: 'paid' | 'pending' | 'failed' | 'expired' | 'reversed';
  entitlementId?: string;
  clientSecret?: string | null;
  idempotent?: boolean;
}

export interface VaultItem {
  entitlement_id: string;
  purchased_at: string;
  beat_id: string;
  name: string;
  bpm: number;
  musical_key: string;
  genres: string[];
  duration_seconds: number | null;
  tender: string;
  amount_points: number | null;
  amount_cents: number | null;
  files: BeatFile[];
  stream_url: string | null;
}

export interface SellerBeat extends Beat {
  rejection_reason: string | null;
  stem_count: number;
  media_ready: boolean;
  created_at: string;
  /** true when the master WAV actually reached storage (draft/processing only) */
  master_uploaded: boolean | null;
  /** true when the master WAV and every required stem reached storage */
  all_required_files_uploaded: boolean | null;
  /** seconds since processing started; null unless status === 'processing' */
  processing_age_seconds: number | null;
  /** processing has run so long it is considered interrupted and retryable */
  processing_stale: boolean;
  /** open listed-edit pending admin decision */
  has_pending_edit?: boolean;
  pending_edit_id?: string | null;
  pending_edit_status?: string | null;
  pending_edit_rejection_reason?: string | null;
}

export interface SellerMeta {
  keys: string[];
  genres: string[];
  pricing_bounds: {
    price_points_min: number; price_points_max: number;
    price_cents_min: number; price_cents_max: number;
  };
  seller_terms_accepted: boolean;
}

export interface SellerSale {
  order_id: string;
  sold_at: string;
  tender: string;
  beat_id: string;
  beat_name: string;
  gross_points: number | null;
  gross_cents: number | null;
  commission_points: number | null;
  commission_cents: number | null;
  net_points: number | null;
  net_cents: number | null;
  status: string;
}

export interface UploadSlot {
  kind: 'master_wav' | 'stem' | 'preview_clip';
  label: string | null;
  upload_url: string;
  content_type: string;
  /** Preview slots are offered to every seller but never block processing. */
  optional?: boolean;
  /** present on re-issued slots: whether this file already reached storage */
  uploaded?: boolean;
}

export type Currency = 'points' | 'cash';
export type SortKey = 'newest' | 'oldest' | 'az' | 'za';

export const fmtPoints = (n: number | null | undefined): string =>
  n == null ? '—' : Number(n).toLocaleString('en-US') + ' ✦';
export const fmtCents = (n: number | null | undefined): string =>
  n == null ? '—' : '$' + (Number(n) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
export const fmtPrice = (beat: { price_points: number; price_cents: number }, cur: Currency): string =>
  cur === 'points' ? fmtPoints(beat.price_points) : fmtCents(beat.price_cents);
export const mmss = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m + ':' + (s < 10 ? '0' : '') + s;
};
